import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMinisiteSessionAnalytics } from '../analytics/webAnalytics';
import {
  fetchMinisiteContinueMobilePhone,
  submitMinisiteContinueMobile,
} from '../api/minisiteContinueMobile';
import './ContinueOnMobileMinisite.css';

const DEFAULT_CC = '+972';
const MAX_NATIONAL_DIGITS = 9;
const KEYPAD_COUNT = 12;

/** Android KeyEvent keyCode (WebView): https://developer.android.com/reference/android/view/KeyEvent */
const KC_DPAD_UP = 19;
const KC_DPAD_DOWN = 20;
const KC_DPAD_LEFT = 21;
const KC_DPAD_RIGHT = 22;
const KC_DPAD_CENTER = 23;
const KC_ENTER = 66;
const KC_NUMPAD_ENTER = 160;
const KC_0 = 7;
const KC_9 = 16;
const KC_DEL = 67; // backspace / delete

/** E.164 or digits → national 9 digits for IL mobile */
function nationalDigitsFromStoredPhone(stored) {
  if (!stored || !String(stored).trim()) return '';
  const raw = String(stored).trim();
  let d = raw.replace(/\D/g, '');
  if (d.startsWith('972') && d.length >= 11) {
    d = d.slice(3);
  }
  if (d.length === 10 && d.startsWith('0')) {
    d = d.slice(1);
  }
  if (d.length === 9 && d.startsWith('5')) {
    return d;
  }
  return '';
}

function nationalDigitsValid(digits) {
  const d = String(digits).replace(/\D/g, '');
  return d.length === 9 && d.startsWith('5');
}

function formatNationalDisplay(rawDigits) {
  const d = String(rawDigits || '')
    .replace(/\D/g, '')
    .slice(0, MAX_NATIONAL_DIGITS);
  if (!d) return '';
  if (d.length <= 3) return d;
  if (d.length <= 6) return `${d.slice(0, 3)}-${d.slice(3)}`;
  return `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}`;
}

/** TV display: 5XXXXXXXX as 0XX - XXX - XXXX (e.g. 054 - 338 - 8082) */
function formatNationalDisplayTv(rawDigits) {
  const d = String(rawDigits || '')
    .replace(/\D/g, '')
    .slice(0, MAX_NATIONAL_DIGITS);
  if (!d) return '';
  if (d[0] === '5') {
    if (d.length <= 2) {
      return `0${d}`;
    }
    if (d.length <= 3) {
      return `0${d.slice(0, 2)} - ${d.slice(2)}`;
    }
    if (d.length <= 6) {
      return `0${d.slice(0, 2)} - ${d.slice(2, 5)}`;
    }
    return `0${d.slice(0, 2)} - ${d.slice(2, 5)} - ${d.slice(5)}`;
  }
  return formatNationalDisplay(rawDigits);
}

function indexToRowCol(index) {
  return { row: Math.floor(index / 3), col: index % 3 };
}

/**
 * Move one step in the grid; if that cell is disabled, keep moving in the same
 * direction until an enabled cell or the edge.
 * @param {number} fromIdx
 * @param {number} dRow
 * @param {number} dCol
 * @param {(i: number) => boolean} isDisabled
 */
function nextEnabledIndex(fromIdx, dRow, dCol, isDisabled) {
  const { row, col } = indexToRowCol(fromIdx);
  let r = row + dRow;
  let c = col + dCol;
  for (let step = 0; step < 12; step += 1) {
    if (r < 0 || r > 3 || c < 0 || c > 2) {
      return fromIdx;
    }
    const idx = r * 3 + c;
    if (!isDisabled(idx)) {
      return idx;
    }
    r += dRow;
    c += dCol;
  }
  return fromIdx;
}

function ContinueOnMobileMinisite() {
  useMinisiteSessionAnalytics();
  const [searchParams] = useSearchParams();
  const deviceId = (searchParams.get('deviceId') || searchParams.get('device_id') || '').trim();

  const [phoneNational, setPhoneNational] = useState('');
  const [phoneLoadState, setPhoneLoadState] = useState('idle');
  const [phoneErr, setPhoneErr] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [successPhone, setSuccessPhone] = useState('');
  const [keypadFocusIndex, setKeypadFocusIndex] = useState(0);
  const eqRef = useRef(null);
  const keypadBtnRefs = useRef([]);
  const userEditedPhone = useRef(false);
  const keypadContainerRef = useRef(null);

  const isKeypadButtonDisabled = useCallback(
    (idx) => {
      const d = String(phoneNational).replace(/\D/g, '');
      if (idx >= 0 && idx <= 8) {
        return submitting || d.length >= MAX_NATIONAL_DIGITS;
      }
      if (idx === 9) {
        return submitting || d.length === 0;
      }
      if (idx === 10) {
        return submitting || d.length >= MAX_NATIONAL_DIGITS;
      }
      if (idx === 11) {
        return submitting;
      }
      return true;
    },
    [phoneNational, submitting]
  );

  const appendDigit = useCallback((digit) => {
    if (!/^[0-9]$/.test(digit)) return;
    userEditedPhone.current = true;
    setPhoneNational((s) => {
      const cur = String(s).replace(/\D/g, '');
      if (cur.length >= MAX_NATIONAL_DIGITS) return s;
      return cur + digit;
    });
    setPhoneErr(false);
    setSubmitError('');
  }, []);

  const backspaceDigit = useCallback(() => {
    userEditedPhone.current = true;
    setPhoneNational((s) => String(s).replace(/\D/g, '').slice(0, -1));
    setPhoneErr(false);
    setSubmitError('');
  }, []);

  useEffect(() => {
    if (!deviceId) return undefined;
    const ac = new AbortController();
    let cancelled = false;
    setPhoneLoadState('loading');
    const timeoutMs = 15000;
    const timeoutId = setTimeout(() => ac.abort(), timeoutMs);

    (async () => {
      try {
        const data = await fetchMinisiteContinueMobilePhone(deviceId, { signal: ac.signal });
        if (cancelled) return;
        const national = nationalDigitsFromStoredPhone(data.phone);
        if (national && !userEditedPhone.current) {
          setPhoneNational(national);
        }
        setPhoneLoadState('done');
      } catch (e) {
        if (cancelled) return;
        const aborted = e && (e.name === 'AbortError' || e.name === 'TimeoutError');
        setPhoneLoadState('error');
        setSubmitError(
          aborted
            ? 'השרת לא הגיב בזמן. נסה שוב.'
            : 'לא ניתן לטעון את מספר הטלפון השמור. נסה שוב או הזן ידנית.'
        );
      } finally {
        clearTimeout(timeoutId);
      }
    })();

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
      ac.abort();
    };
  }, [deviceId]);

  useEffect(() => {
    const id = 'continue-mobile-heebo-font';
    if (!document.getElementById(id)) {
      const link = document.createElement('link');
      link.id = id;
      link.rel = 'stylesheet';
      link.href =
        'https://fonts.googleapis.com/css2?family=Heebo:wght@400;600;800;900&display=swap';
      document.head.appendChild(link);
    }
  }, []);

  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const root = document.getElementById('root');
    const prev = {
      htmlOverflow: html.style.overflow,
      htmlHeight: html.style.height,
      bodyOverflow: body.style.overflow,
      bodyHeight: body.style.height,
      bodyOverscroll: body.style.overscrollBehavior,
      rootHeight: root ? root.style.height : '',
      rootMinHeight: root ? root.style.minHeight : '',
      rootOverflow: root ? root.style.overflow : '',
    };
    html.style.overflow = 'hidden';
    html.style.height = '100%';
    body.style.overflow = 'hidden';
    body.style.height = '100%';
    body.style.overscrollBehavior = 'none';
    if (root) {
      root.style.height = '100%';
      root.style.minHeight = '100dvh';
      root.style.overflow = 'hidden';
    }
    return () => {
      html.style.overflow = prev.htmlOverflow;
      html.style.height = prev.htmlHeight;
      body.style.overflow = prev.bodyOverflow;
      body.style.height = prev.bodyHeight;
      body.style.overscrollBehavior = prev.bodyOverscroll;
      if (root) {
        root.style.height = prev.rootHeight;
        root.style.minHeight = prev.rootMinHeight;
        root.style.overflow = prev.rootOverflow;
      }
    };
  }, []);

  useEffect(() => {
    const el = eqRef.current;
    if (!el) return;
    el.innerHTML = '';
    for (let i = 0; i < 40; i++) {
      const bar = document.createElement('div');
      bar.className = 'eq-bar';
      bar.style.height = `${Math.random() * 60 + 15}%`;
      bar.style.animationDelay = `${Math.random() * 1.4}s`;
      bar.style.animationDuration = `${0.8 + Math.random() * 0.9}s`;
      el.appendChild(bar);
    }
  }, []);

  const handleSubmit = useCallback(async () => {
    setSubmitError('');
    const digits = phoneNational.replace(/\D/g, '');
    if (!digits || !nationalDigitsValid(digits)) {
      setPhoneErr(true);
      return;
    }

    if (!deviceId) {
      setSubmitError('חסר מזהה מכשיר. פתח את העמוד מהטלוויזיה.');
      return;
    }

    setSubmitting(true);
    try {
      const data = await submitMinisiteContinueMobile(deviceId, DEFAULT_CC, digits);
      const display = data.phone || `${DEFAULT_CC} ${digits}`;
      setSuccessPhone(display);
      setSuccess(true);
    } catch (err) {
      setSubmitError(err.message || 'שגיאה בשמירה. נסה שוב.');
    } finally {
      setSubmitting(false);
    }
  }, [deviceId, phoneNational]);

  const appendDigitRef = useRef(appendDigit);
  appendDigitRef.current = appendDigit;
  const backspaceDigitRef = useRef(backspaceDigit);
  backspaceDigitRef.current = backspaceDigit;
  const handleSubmitRef = useRef(handleSubmit);
  handleSubmitRef.current = handleSubmit;
  const isKeypadButtonDisabledRef = useRef(isKeypadButtonDisabled);
  isKeypadButtonDisabledRef.current = isKeypadButtonDisabled;
  const keypadFocusIndexRef = useRef(0);
  keypadFocusIndexRef.current = keypadFocusIndex;
  const setKeypadFocusIndexRef = useRef(setKeypadFocusIndex);
  setKeypadFocusIndexRef.current = setKeypadFocusIndex;

  useEffect(() => {
    if (!deviceId || success) return undefined;

    const onKeyDown = (e) => {
      if (success) return;
      const kc = e.keyCode != null ? e.keyCode : e.which;
      const key = e.key;
      const inKeypad = (el) =>
        el &&
        el.tagName === 'BUTTON' &&
        typeof el.closest === 'function' &&
        el.closest('.continue-mobile-tv-keypad');

      const tryDigitFromKeyCode = (code) => {
        if (code >= KC_0 && code <= KC_9) {
          return String.fromCharCode(48 + (code - KC_0));
        }
        return null;
      };

      if (
        kc === KC_DPAD_UP ||
        kc === KC_DPAD_DOWN ||
        kc === KC_DPAD_LEFT ||
        kc === KC_DPAD_RIGHT
      ) {
        e.preventDefault();
        e.stopPropagation();
        const from = keypadFocusIndexRef.current;
        let dRow = 0;
        let dCol = 0;
        if (kc === KC_DPAD_UP) dRow = -1;
        if (kc === KC_DPAD_DOWN) dRow = 1;
        if (kc === KC_DPAD_LEFT) dCol = -1;
        if (kc === KC_DPAD_RIGHT) dCol = 1;
        const next = nextEnabledIndex(from, dRow, dCol, (i) =>
          isKeypadButtonDisabledRef.current(i)
        );
        setKeypadFocusIndexRef.current(next);
        const nEl = keypadBtnRefs.current[next];
        if (nEl) {
          try {
            nEl.focus({ preventScroll: true });
          } catch (_) {
            /* ignore */
          }
        }
        return;
      }

      if (key === 'ArrowUp' || key === 'ArrowDown' || key === 'ArrowLeft' || key === 'ArrowRight') {
        e.preventDefault();
        e.stopPropagation();
        const from = keypadFocusIndexRef.current;
        let dRow = 0;
        let dCol = 0;
        if (key === 'ArrowUp') dRow = -1;
        if (key === 'ArrowDown') dRow = 1;
        if (key === 'ArrowLeft') dCol = -1;
        if (key === 'ArrowRight') dCol = 1;
        const next = nextEnabledIndex(from, dRow, dCol, (i) =>
          isKeypadButtonDisabledRef.current(i)
        );
        setKeypadFocusIndexRef.current(next);
        const nEl = keypadBtnRefs.current[next];
        if (nEl) {
          try {
            nEl.focus({ preventScroll: true });
          } catch (_) {
            /* ignore */
          }
        }
        return;
      }

      const digitFromAndroid = tryDigitFromKeyCode(kc);
      if (digitFromAndroid) {
        e.preventDefault();
        e.stopPropagation();
        appendDigitRef.current(digitFromAndroid);
        return;
      }

      if (key >= '0' && key <= '9') {
        e.preventDefault();
        appendDigitRef.current(key);
        return;
      }
      if (e.code && /^Digit[0-9]$/.test(e.code)) {
        e.preventDefault();
        appendDigitRef.current(e.code.slice(-1));
        return;
      }
      if (e.code && /^Numpad[0-9]$/.test(e.code)) {
        e.preventDefault();
        appendDigitRef.current(e.code.slice(-1));
        return;
      }
      if (key === 'Backspace' || kc === KC_DEL) {
        e.preventDefault();
        backspaceDigitRef.current();
        return;
      }

      if (
        key === 'Enter' ||
        e.code === 'NumpadEnter' ||
        kc === KC_ENTER ||
        kc === KC_NUMPAD_ENTER ||
        kc === KC_DPAD_CENTER
      ) {
        e.preventDefault();
        e.stopPropagation();
        const el = document.activeElement;
        if (inKeypad(el)) {
          const btn = el;
          btn.click();
          const refocus = () => {
            try {
              if (btn.isConnected && !btn.disabled) {
                btn.focus({ preventScroll: true });
              }
            } catch (_) {
              /* ignore */
            }
          };
          requestAnimationFrame(() => {
            refocus();
            requestAnimationFrame(refocus);
          });
          setTimeout(refocus, 0);
          return;
        }
        handleSubmitRef.current();
      }
    };

    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [deviceId, success]);

  useEffect(() => {
    if (!deviceId || success) return;
    if (phoneLoadState !== 'done' && phoneLoadState !== 'error') return;
    const t = setTimeout(() => {
      try {
        let idx = 0;
        for (let i = 0; i < KEYPAD_COUNT; i += 1) {
          if (!isKeypadButtonDisabled(i)) {
            idx = i;
            break;
          }
        }
        setKeypadFocusIndex(idx);
        keypadBtnRefs.current[idx]?.focus({ preventScroll: true });
      } catch (_) {
        /* ignore */
      }
    }, 100);
    return () => clearTimeout(t);
  }, [deviceId, success, phoneLoadState, isKeypadButtonDisabled]);

  if (!deviceId) {
    return (
      <div className="continue-mobile" lang="he" dir="rtl">
        <div className="tv-root">
          <div className="bg-grid" aria-hidden />
          <div className="bg-glow" aria-hidden />
          <div className="continue-mobile-missing-device">
            <h1>לא ניתן להמשיך</h1>
            <p>חסר פרמטר מזהה מכשיר (deviceId). פתח את העמוד מהאפליקציה בטלוויזיה.</p>
          </div>
        </div>
      </div>
    );
  }

  const displayTv = formatNationalDisplayTv(phoneNational);
  const nationalPlain = phoneNational.replace(/\D/g, '');
  const isValid = nationalPlain.length > 0 && nationalDigitsValid(nationalPlain);

  return (
    <div className="continue-mobile" lang="he" dir="rtl">
      <div className="tv-root">
        <div className="bg-grid" aria-hidden />
        <div className="bg-glow" aria-hidden />
        <div className="eq-bars" ref={eqRef} id="eqBars" aria-hidden />

        <div className="main-card continue-mobile-main-card--tv">
          <div className="right-panel">
            <div className={`form-state ${success ? 'continue-mobile-form--hidden' : ''}`} id="formState">
              <div>
                <div className="step-badge">
                  <div className="step-dot" />
                  <span className="step-text">המשך בנייד</span>
                </div>
              </div>

              <h1 className="continue-mobile-title-tv">הכנס מספר טלפון</h1>
              <p className="sub-copy sub-copy--compact">
                השתמשו בשלט: חצים לניווט, Enter לאישור, או הזינו ספרות.
              </p>

              {phoneLoadState === 'loading' ? (
                <p className="continue-mobile-loading-note">טוען פרטים…</p>
              ) : null}

              <div className="continue-mobile-phone-block">
                <div
                  className={`continue-mobile-phone-display-wrap ${phoneErr ? 'continue-mobile-phone-display-wrap--err' : ''} ${isValid ? 'continue-mobile-phone-display-wrap--valid' : ''}`}
                  dir="ltr"
                >
                  <span className="continue-mobile-phone-bracket" aria-hidden>
                    [
                  </span>
                  <div
                    className={`continue-mobile-phone-display ${phoneErr ? 'continue-mobile-phone-display--err' : ''} ${isValid ? 'continue-mobile-phone-display--valid' : ''}`}
                    role="status"
                    aria-live="polite"
                    aria-label="תצוגת מספר טלפון"
                    onFocus={(ev) => {
                      ev.preventDefault();
                      const idx = keypadFocusIndexRef.current;
                      keypadBtnRefs.current[idx]?.focus();
                    }}
                  >
                    <span className="continue-mobile-phone-cc">{DEFAULT_CC}</span>
                    <span className="continue-mobile-phone-sep"> </span>
                    <span className="continue-mobile-phone-digits">
                      {displayTv || (
                        <span className="continue-mobile-phone-placeholder">הזינו 9 ספרות</span>
                      )}
                    </span>
                  </div>
                  <span className="continue-mobile-phone-bracket" aria-hidden>
                    ]
                  </span>
                </div>

                <div
                  className="continue-mobile-tv-keypad"
                  ref={keypadContainerRef}
                  role="group"
                  aria-label="מקלדת ספרות"
                >
                  {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((d, i) => {
                    const disabled = isKeypadButtonDisabled(i);
                    return (
                      <button
                        key={`d-${d}`}
                        type="button"
                        ref={(el) => {
                          keypadBtnRefs.current[i] = el;
                        }}
                        tabIndex={-1}
                        className={`continue-mobile-keypad-btn continue-mobile-keypad-btn--digit${
                          keypadFocusIndex === i ? ' continue-mobile-keypad-btn--focus' : ''
                        }`}
                        onClick={() => !disabled && appendDigit(d)}
                        disabled={disabled}
                        onFocus={() => setKeypadFocusIndex(i)}
                        aria-label={`ספרה ${d}`}
                      >
                        {d}
                      </button>
                    );
                  })}
                  <button
                    type="button"
                    ref={(el) => {
                      keypadBtnRefs.current[9] = el;
                    }}
                    tabIndex={-1}
                    className={`continue-mobile-keypad-btn continue-mobile-keypad-btn--action${
                      keypadFocusIndex === 9 ? ' continue-mobile-keypad-btn--focus' : ''
                    }`}
                    onClick={() => !isKeypadButtonDisabled(9) && backspaceDigit()}
                    disabled={isKeypadButtonDisabled(9)}
                    onFocus={() => setKeypadFocusIndex(9)}
                    aria-label="מחק ספרה"
                  >
                    ⌫
                  </button>
                  <button
                    type="button"
                    ref={(el) => {
                      keypadBtnRefs.current[10] = el;
                    }}
                    tabIndex={-1}
                    className={`continue-mobile-keypad-btn continue-mobile-keypad-btn--digit${
                      keypadFocusIndex === 10 ? ' continue-mobile-keypad-btn--focus' : ''
                    }`}
                    onClick={() => !isKeypadButtonDisabled(10) && appendDigit('0')}
                    disabled={isKeypadButtonDisabled(10)}
                    onFocus={() => setKeypadFocusIndex(10)}
                    aria-label="אפס"
                  >
                    0
                  </button>
                  <button
                    type="button"
                    ref={(el) => {
                      keypadBtnRefs.current[11] = el;
                    }}
                    tabIndex={-1}
                    className={`continue-mobile-keypad-btn continue-mobile-keypad-btn--ok${
                      keypadFocusIndex === 11 ? ' continue-mobile-keypad-btn--focus' : ''
                    }`}
                    onClick={() => {
                      if (!isKeypadButtonDisabled(11)) handleSubmit();
                    }}
                    disabled={isKeypadButtonDisabled(11)}
                    onFocus={() => setKeypadFocusIndex(11)}
                    aria-label="שלח"
                  >
                    {submitting ? '…' : '✔'}
                  </button>
                </div>
              </div>

              {submitError ? <p className="continue-mobile-err-banner">{submitError}</p> : null}

              <p className="privacy-note">המספר ישמש רק ליצירת קשר. ללא ספאם.</p>
            </div>

            <div
              className={`success-state ${success ? 'continue-mobile-success--visible' : ''}`}
              id="successState"
              aria-live="polite"
            >
              <div className="success-icon">✓</div>
              <div className="success-title">הפרטים נשמרו</div>
              <div className="success-phone" id="successPhone">
                {successPhone}
              </div>
              <p className="success-msg">
                נחזור אליך בהקדם.
                <br />
                תמשיך ליהנות מהמוזיקה על הטלוויזיה בינתיים.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ContinueOnMobileMinisite;
